/*
 * TableEventBus - Event-driven table management inspired by CypherPoker.JS
 * BUDB - Professional poker event dispatching
 * Developed by GRUDGE STUDIO
 */

export type TableEventType =
  | 'tablenew'
  | 'tablejoin'
  | 'tableready'
  | 'tablemsg'
  | 'tableleave'
  | 'preDeal'
  | 'dealHole'       // Hole cards dealt to players
  | 'dealFlop'       // First 3 community cards
  | 'dealTurn'       // 4th community card
  | 'dealRiver'      // 5th community card
  | 'showdown'       // Cards revealed
  | 'playerAction'
  | 'potUpdate'
  | 'roundChange'
  | 'handComplete';

export interface TableEvent {
  type: TableEventType;
  tableId: string;
  timestamp: number;
  data: any;
}

export interface GameStateSnapshot {
  pot: number;
  round: string;
  communityCards: any[];
  seats: any[];
  handInProgress: boolean;
  currentBet: number;
  activePlayerIndex: number;
}

type EventCallback = (event: TableEvent) => void;

class TableEventBus {
  private listeners: Map<TableEventType, Set<EventCallback>> = new Map();
  private lastSnapshot: GameStateSnapshot | null = null;
  private animationQueue: TableEvent[] = [];
  private isProcessing = false;

  subscribe(eventType: TableEventType, callback: EventCallback): () => void {
    if (!this.listeners.has(eventType)) {
      this.listeners.set(eventType, new Set());
    }
    this.listeners.get(eventType)!.add(callback);
    
    return () => {
      this.listeners.get(eventType)?.delete(callback);
    };
  }

  subscribeAll(callback: EventCallback): () => void {
    const unsubscribers: (() => void)[] = [];
    const allTypes: TableEventType[] = [
      'tablenew', 'tablejoin', 'tableready', 'tablemsg', 'tableleave',
      'preDeal', 'dealHole', 'dealFlop', 'dealTurn', 'dealRiver',
      'showdown', 'playerAction', 'potUpdate', 'roundChange', 'handComplete'
    ];
    
    allTypes.forEach(type => {
      unsubscribers.push(this.subscribe(type, callback));
    });
    
    return () => unsubscribers.forEach(unsub => unsub());
  }

  dispatch(event: TableEvent): void {
    this.animationQueue.push(event);
    this.processQueue();
  }

  private async processQueue(): Promise<void> {
    if (this.isProcessing || this.animationQueue.length === 0) return;
    
    this.isProcessing = true;
    
    while (this.animationQueue.length > 0) {
      const event = this.animationQueue.shift()!;
      const listeners = this.listeners.get(event.type);
      
      if (listeners) {
        listeners.forEach(callback => {
          try {
            callback(event);
          } catch (error) {
            console.error('TableEventBus callback error:', error);
          }
        });
      }
      
      // Small delay between events for animation sequencing
      if (this.animationQueue.length > 0) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    }
    
    this.isProcessing = false;
  }

  processStateUpdate(tableId: string, newState: GameStateSnapshot): void {
    const prev = this.lastSnapshot;
    
    // Normalize round strings to lowercase for consistent comparison
    const currentRound = newState.round?.toLowerCase() || 'waiting';
    const normalizedState = { ...newState, round: currentRound };
    
    if (!prev) {
      this.lastSnapshot = normalizedState;
      if (normalizedState.handInProgress) {
        this.dispatch({
          type: 'preDeal',
          tableId,
          timestamp: Date.now(),
          data: normalizedState
        });
        
        // Dispatch dealHole for hole card animations
        if (currentRound === 'preflop') {
          this.dispatch({
            type: 'dealHole',
            tableId,
            timestamp: Date.now(),
            data: { seats: normalizedState.seats }
          });
        }
      }
      return;
    }

    const prevRound = prev.round?.toLowerCase() || 'waiting';

    // Detect round changes
    if (prevRound !== currentRound) {
      this.dispatch({
        type: 'roundChange',
        tableId,
        timestamp: Date.now(),
        data: { from: prevRound, to: currentRound }
      });

      // Dispatch dealHole when transitioning to preflop
      if (currentRound === 'preflop' && prevRound === 'waiting') {
        this.dispatch({
          type: 'dealHole',
          tableId,
          timestamp: Date.now(),
          data: { seats: normalizedState.seats }
        });
      }

      // Dispatch specific card dealing events
      if (currentRound === 'flop' && prevRound === 'preflop') {
        this.dispatch({
          type: 'dealFlop',
          tableId,
          timestamp: Date.now(),
          data: { cards: normalizedState.communityCards.slice(0, 3) }
        });
      } else if (currentRound === 'turn' && prevRound === 'flop') {
        this.dispatch({
          type: 'dealTurn',
          tableId,
          timestamp: Date.now(),
          data: { card: normalizedState.communityCards[3] }
        });
      } else if (currentRound === 'river' && prevRound === 'turn') {
        this.dispatch({
          type: 'dealRiver',
          tableId,
          timestamp: Date.now(),
          data: { card: normalizedState.communityCards[4] }
        });
      } else if (currentRound === 'showdown') {
        this.dispatch({
          type: 'showdown',
          tableId,
          timestamp: Date.now(),
          data: normalizedState
        });
      }
    }

    // Detect pot changes
    if (prev.pot !== normalizedState.pot) {
      this.dispatch({
        type: 'potUpdate',
        tableId,
        timestamp: Date.now(),
        data: { from: prev.pot, to: normalizedState.pot }
      });
    }

    // Detect hand completion
    if (prev.handInProgress && !normalizedState.handInProgress) {
      this.dispatch({
        type: 'handComplete',
        tableId,
        timestamp: Date.now(),
        data: normalizedState
      });
    }

    // Detect new hand starting
    if (!prev.handInProgress && normalizedState.handInProgress) {
      this.dispatch({
        type: 'preDeal',
        tableId,
        timestamp: Date.now(),
        data: normalizedState
      });
    }

    this.lastSnapshot = normalizedState;
  }

  reset(): void {
    this.lastSnapshot = null;
    this.animationQueue = [];
    this.isProcessing = false;
  }
}

export const tableEventBus = new TableEventBus();
